import React, { } from 'react';
import PipsPageFrame from './PipsPageFrame';
import { animate, mix, motion, useMotionTemplate, useMotionValue, useTime, useTransform } from 'motion/react';
import { Link } from 'react-router';

const previewsRoot = '/pips/previews/';

interface VarietyItemProps {
    variety: string,
    src: string,
    delay: number
}


const VarietyItem: React.FC<VarietyItemProps> = ({ variety, src, delay }) => {
    return (
        <div
            className={'w-4/5 flex grow gap-x-4 items-center'}
        >
            <motion.img
                layout
                src={src}
                className={'size-24 sm:size-36 rounded-full border-white border-4 shadow-2xl'}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0, transition: { duration: .2 + delay, delay: delay, ease: 'easeOut' } }}
            />

            <motion.h2
                layout
                className={'text-white text-sun text-2xl'}
                style={{ filter: 'drop-shadow(0 0 8px black)' }}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0, transition: { duration: .2 + delay, delay: delay + .05, ease: 'easeOut' } }}
            >{variety}</motion.h2>
        </div>
    )
}


const VarietiesBox: React.FC = () => {
    const bgOpacity = useMotionValue(.35);

    return (
        <Link to='products/general'>
            <motion.div
                className='w-full rounded-md overflow-clip relative'
                onHoverStart={() => animate(bgOpacity, .50)}
                onHoverEnd={() => animate(bgOpacity, .35)}
            >
                {/* Background */}
                <motion.div className='absolute top-0 left-0 size-full bg-black pointer-events-none' style={{ opacity: bgOpacity }} />

                {/* Content */}
                <div className='w-full flex flex-col items-center gap-y-6 p-4 relative z-10'>
                    <h2
                        className='text-2xl text-revamped font-semibold bg-clip-text text-transparent bg-gradient-to-br from-pink-400 to-sky-400'
                        style={{ filter: 'drop-shadow(0 0 8px black)' }}
                    ><span className='font-bold'>500+</span> Varieties</h2>
                    <VarietyItem src={`${previewsRoot}acrylic.jpg`} variety='Acrylic' delay={0} />
                    <VarietyItem src={`${previewsRoot}glow.jpg`} variety='Glow-in-the-dark' delay={.2} />
                    <VarietyItem src={`${previewsRoot}liquid_core.jpg`} variety='Liquid Core' delay={.4} />
                    <VarietyItem src={`${previewsRoot}metal_2.jpg`} variety='Metal' delay={.6} />
                    <h2
                        className='text-2xl text-sun font-semibold text-white'
                        style={{ filter: 'drop-shadow(0 0 8px black)' }}
                    >...and many more!</h2>
                </div>

            </motion.div>
        </Link>
    )
}


const degreeRange = mix(0, 360)
const MysteryBox: React.FC = () => {
    const borderAnimationSpeed = useMotionValue(2000) // ms;
    const bgOpacity = useMotionValue(.30);

    const t = useTime();
    const borderRotateZ = useTransform(() => degreeRange(t.get() / borderAnimationSpeed.get()))
    const borderRotateTemplate = useMotionTemplate`${borderRotateZ}deg`

    // const pathLengthDuration = useState()
    const pathLength = useMotionValue(.5);

    const handleOnHoverStart = () => {
        // animate(borderAnimationSpeed, 3000, { ease: 'easeIn' });
        borderAnimationSpeed.jump(250)
        animate(bgOpacity, .05);
    }

    const handleOnHoverEnd = () => {
        // animate(borderAnimationSpeed, 2000, { ease: 'easeIn' });
        borderAnimationSpeed.jump(2000);
        animate(bgOpacity, .30)
    }

    return (
        <Link to='products/mystery'>
            <motion.div
                className='w-full h-full grow flex flex-col items-center justify-between p-4 gap-y-12 rounded-md overflow-clip relative hover:cursor-pointer'
                style={{ background: 'url(/pips/mystery_bg.jpg', backgroundSize: 'cover' }}
                onHoverStart={handleOnHoverStart}
                onHoverEnd={handleOnHoverEnd}
            >
                {/* Background */}
                <motion.div className='absolute top-0 left-0 size-full bg-black pointer-events-none' style={{ opacity: bgOpacity }} />

                {/* Header */}
                <div className='flex flex-col items-center gap-y-1'>
                    <h2 className='text-2xl text-sun font-semibold text-white'>grab a bag of</h2>
                    <h2
                        className='text-3xl text-revamped font-semibold'
                        style={{ color: '#f8f4fe', filter: 'drop-shadow(0 0 8px #b09eac)' }}
                    >MYSTERY DICE
                    </h2>
                </div>

                {/* Question Mark */}
                <div
                    className='rounded-full border-4 size-24 sm:size-32 aspect-square border-gray-700 flex justify-center p-4 relative'
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 640 640"
                        className='h-full'
                    >
                        <path
                            fill='white'
                            d="M224 224C224 171 267 128 320 128C373 128 416 171 416 224C416 266.7 388.1 302.9 349.5 315.4C321.1 324.6 288 350.7 288 392L288 416C288 433.7 302.3 448 320 448C337.7 448 352 433.7 352 416L352 392C352 390.3 352.6 387.9 355.5 384.7C358.5 381.4 363.4 378.2 369.2 376.3C433.5 355.6 480 295.3 480 224C480 135.6 408.4 64 320 64C231.6 64 160 135.6 160 224C160 241.7 174.3 256 192 256C209.7 256 224 241.7 224 224zM320 576C342.1 576 360 558.1 360 536C360 513.9 342.1 496 320 496C297.9 496 280 513.9 280 536C280 558.1 297.9 576 320 576z"
                        />
                    </svg>
                    <motion.svg
                        className='absolute top-0 left-0 size-full'
                        style={{ rotateZ: borderRotateTemplate }}
                        viewBox='0 0 128 128'
                    >
                        <motion.circle
                            cx='64' cy='64' r='62'
                            strokeWidth={4}
                            stroke='white'
                            fill='transparent'
                            style={{ pathLength }}
                        />
                    </motion.svg>
                </div>

                {/* Outro */}
                <div></div>

            </motion.div>
        </Link>
    )
}

const PipsHome: React.FC = () => {
    return (
        <PipsPageFrame>
            <div className='w-full flex flex-col justify-center items-center gap-y-12 px-6'>
                <h2
                    className='text-2xl text-revamped font-semibold pt-6 bg-clip-text text-transparent bg-gradient-to-br relative z-0 from-pink-300 to-pink-600'
                    style={{ filter: 'drop-shadow(0 0 16px black)' }}
                >Leave your rolls to chance, not the quality of your dice.</h2>


                {/* Selection */}
                <div className='w-full flex flex-col sm:grid sm:grid-cols-2 gap-2 relative'>
                    <VarietiesBox />
                    <div className='flex w-full sm:hidden relative'>
                        <div className='absolute top-0 left-0 w-full flex justify-center items-center  z-50'>
                            <p className='-mt-3 drop-shadow-md text-white font-semibold text-teko text-3xl'>— OR —</p>
                        </div>
                    </div>
                    <MysteryBox />

                    <div className='hidden sm:flex absolute top-0 left-0 size-full justify-center items-center pointer-events-none'>
                        <div className='text-white font-semibold text-teko text-2xl relative z-50'>— OR —</div>
                    </div>
                </div>
            </div>
        </PipsPageFrame>
    )
}

// 
// Liquid Metal
// Gemstone
// Glow in the dark
// Acrylic
// Metal

// ... and many more!

export default PipsHome;